import * as React from 'react';
import { useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import { Ratings } from '@notino/react-styleguide';
import {
  GetReviewOverviewQuery,
  GetReviewOverviewQueryVariables,
} from '@notino/shared/definitions/types';

import { useTrackingContext } from '@context/tracking/TrackingContext';
import { TabLink } from '@sharedComponents/TabSwitcher';

import getReviewOverviewQuery from '../../queries/reviewOverview.graphql';
import { TabIds } from '../model';

import { messages } from './messages';
import {
  AvgRating,
  RatingsCount,
  RatingWrapper,
  ReviewsAnchor,
} from './styled';
import { trackRatingClick } from './utils';

interface IPlaceholderProps {
  catalogMasterId: string;
  reviewsId?: string;
}

export const ProductRating = ({
  catalogMasterId,
  reviewsId = 'productBaseInfo',
}: IPlaceholderProps) => {
  const { getTimeFromInit } = useTrackingContext();
  const { formatMessage, formatNumber } = useIntl();
  const { loading, error, data } = useQuery<
    GetReviewOverviewQuery,
    GetReviewOverviewQueryVariables
  >(getReviewOverviewQuery, {
    ssr: true,
    errorPolicy: 'all',
    variables: {
      catalogMasterId,
    },
  });

  const reviewOverview = data?.productDetailByCatalogMasterId?.reviewOverview;

  const isReviewOverviewLoaded = !loading && !error && reviewOverview;

  const rating = isReviewOverviewLoaded ? reviewOverview.rating : 4.0;
  const totalVotes = isReviewOverviewLoaded ? reviewOverview.count : 1;

  const formattedRating = formatNumber(rating, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <TabLink tabId={TabIds.Reviews}>
      {({ setActiveTab }) => {
        const handleReviewClick = () => {
          trackRatingClick({ timing: getTimeFromInit() });
          setActiveTab();
        };

        return (
          <ReviewsAnchor
            href="#pdReviewsScroll"
            onClick={handleReviewClick}
            aria-label={formatMessage(messages.productRatingAriaLabel, {
              rating: formattedRating,
              count: totalVotes,
            })}
          >
            <RatingWrapper>
              <Ratings
                ratingId={reviewsId}
                rating={rating}
                size={16}
                foreground="text.primary"
                animationDuration={0.5}
                microdata={false}
              />
              {totalVotes > 0 && <AvgRating>{formattedRating}</AvgRating>}
              <RatingsCount>
                (<span>{totalVotes}</span>)
              </RatingsCount>
            </RatingWrapper>
          </ReviewsAnchor>
        );
      }}
    </TabLink>
  );
};
